import React from 'react';
import {
  CheckCircle2, XCircle, Info, AlertTriangle, X,
  TicketCheck, Receipt, Mail, Files, CalendarDays, Zap
} from 'lucide-react';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  modul?: string;
}

interface ToastProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

const TYPE_CONFIG: Record<string, { icon: React.ElementType; bg: string; border: string; text: string; iconColor: string }> = {
  success: { icon: CheckCircle2, bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', iconColor: 'text-emerald-500' },
  error: { icon: XCircle, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', iconColor: 'text-red-500' },
  info: { icon: Info, bg: 'bg-[#fdfbf8]', border: 'border-[#c4a77d]/30', text: 'text-[#0f172a]', iconColor: 'text-[#c4a77d]' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', iconColor: 'text-amber-500' },
};

const MODUL_ICONS: Record<string, React.ElementType> = {
  tickets: TicketCheck,
  rechnungen: Receipt,
  emails: Mail,
  dokumente: Files,
  kalender: CalendarDays,
  system: Zap,
};

const Toast: React.FC<ToastProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[500] flex flex-col-reverse gap-3 max-w-[380px]">
      {toasts.slice(0, 3).map((toast) => {
        const config = TYPE_CONFIG[toast.type] || TYPE_CONFIG.info;
        const TypeIcon = config.icon;
        const ModulIcon = toast.modul ? MODUL_ICONS[toast.modul] : null;

        return (
          <div
            key={toast.id}
            className={`${config.bg} ${config.border} border rounded-2xl shadow-[0_20px_50px_rgba(15,23,42,0.15)] p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 duration-300`}
          >
            <div className={`shrink-0 mt-0.5 ${config.iconColor}`}>
              {ModulIcon ? <ModulIcon size={18} strokeWidth={2} /> : <TypeIcon size={18} strokeWidth={2} />}
            </div>
            <div className="flex-1 min-w-0">
              {toast.modul && (
                <span className="text-[8px] font-black uppercase tracking-[0.15em] text-gray-400 block mb-0.5">
                  {toast.modul}
                </span>
              )}
              <p className={`text-xs font-bold ${config.text} leading-relaxed`}>
                {toast.message}
              </p>
            </div>
            <button
              onClick={() => onDismiss(toast.id)}
              className="shrink-0 text-gray-300 hover:text-gray-500 transition-colors mt-0.5"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default Toast;
